import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Position } from 'geojson';
import { SpatialFilter } from '@carto/react-ui';
import { getCartoSpatialFilterForLineId, getSelectedLasso } from 'store/carto/selectors';
import { PlannerMapDataElement } from 'components/common/types/Planner.types';
import { Store } from 'components/common/types/Store.types';
import { Asset } from 'components/common/types/Asset.types';
import {
  assignPolygonId,
  clearLassoSelection,
  removeNewLassoSelection,
  setFeatureSelectionEnabledForLine,
} from 'store/carto/reducer';
import { v4 as uuidv4 } from 'uuid';
import { addToastNotification } from 'store/notification/reducer';
import { getIsDirectSalesCampaignType } from 'store/dealManagement/selectors/getCampaignType';
import { isPointInsidePolygon } from './isPointInsidePolygon';
import { FurnitureItem } from './useFurniture.types';
import { formatAssetsInformation, formatFurnitureItem } from './util';
import useUpdateDirectSalesCampaign from '../hooks/DirectSalesPlannerActions/useUpdateDirectSalesCampaign';

type UseCartoSelectionTool = {
  getLassoFurnitures: (data: PlannerMapDataElement[]) => FurnitureItem[];
  cartoSelectionSpatialFilter: SpatialFilter | null;
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export const useCartoSelectionTool = (currentLineId: string): UseCartoSelectionTool => {
  const dispatch = useDispatch();

  const { saveLinePolygon } = useUpdateDirectSalesCampaign();
  const spatialFilters = useSelector(getCartoSpatialFilterForLineId(currentLineId));
  const selectedLasso = useSelector(getSelectedLasso);
  const isDirectSalesCampaignType = useSelector(getIsDirectSalesCampaignType);

  const routeFrameCodes = isDirectSalesCampaignType
    ? useSelector((state: Store) => state.dealManagement.commonDeal.currentLine.mapRouteFrameCodes)
    : useSelector((state: Store) => state.dealManagement.commonDeal.currentLine.routeFrameCodes);
  const { currencyCode, localeCode } = useSelector((state: Store) => state.publisher.configuration);

  const getLassoFurnitures = useCallback(
    // eslint-disable-next-line sonarjs/cognitive-complexity
    (data: PlannerMapDataElement[]): FurnitureItem[] => {
      if (!spatialFilters?.length || selectedLasso.anchor === null) return [];

      const spatialFilter = selectedLasso.polygonId
        ? spatialFilters.find((spatialFilterItem) => spatialFilterItem.polygonId === selectedLasso.polygonId)
        : spatialFilters[spatialFilters.length - 1];

      if (!spatialFilter) return [];

      const groupedAssetsInsidePolygon = data.reduce((groups, mapElement) => {
        const isClusterAndHaveEveryAssetsInsidePolygon =
          !!mapElement.properties.cluster &&
          mapElement.properties.assets?.every((assetInCluster) =>
            isPointInsidePolygon(assetInCluster, spatialFilter.geometry.coordinates[0]),
          );
        if (
          isClusterAndHaveEveryAssetsInsidePolygon ||
          isPointInsidePolygon(mapElement.geometry.coordinates as Position, spatialFilter.geometry.coordinates[0])
        ) {
          const { furnitureCode, latitude, longitude, address, postcode, district } = mapElement.properties;
          const assetKey = isDirectSalesCampaignType ? furnitureCode : `${latitude}, ${longitude}`;

          if (!assetKey) return groups;

          const found = groups.get(assetKey);
          if (found) {
            found.assetsInformation.push(
              formatAssetsInformation({
                asset: mapElement.properties as Asset,
                localeCode,
                currencyCode,
                routeFrameCodes: routeFrameCodes ?? [],
                shouldConvertPercentage: isDirectSalesCampaignType,
              }),
            );
          } else {
            groups.set(assetKey, {
              ...formatFurnitureItem(assetKey, { longitude, latitude, address, postcode, district }),
              assetsInformation: [
                formatAssetsInformation({
                  asset: mapElement.properties as Asset,
                  localeCode,
                  currencyCode,
                  routeFrameCodes: routeFrameCodes ?? [],
                  shouldConvertPercentage: isDirectSalesCampaignType,
                }),
              ],
            });
          }
        }

        return groups;
      }, new Map<string, FurnitureItem>());

      const areFramesInsidePolygon = groupedAssetsInsidePolygon.size > 0;

      if (!areFramesInsidePolygon && !spatialFilter.polygonId) {
        dispatch(removeNewLassoSelection(currentLineId));

        dispatch(
          addToastNotification({ header: 'No frames available for selected area', type: 'Warning', timeout: 2000 }),
        );
      } else if (!spatialFilter.polygonId) {
        const polygonId = uuidv4();

        if (isDirectSalesCampaignType) {
          try {
            const lassoData = {
              shape: spatialFilter.properties?.shape || 'Undefined',
              coordinates: spatialFilter.geometry.coordinates[0],
            };

            saveLinePolygon(polygonId, lassoData);
          } catch {} // eslint-disable-line no-empty
        }

        dispatch(assignPolygonId({ lineId: currentLineId, polygonId }));
      }

      return Array.from(groupedAssetsInsidePolygon.values());
    },
    [spatialFilters, routeFrameCodes, localeCode, currencyCode, selectedLasso],
  );

  useEffect(() => {
    dispatch(setFeatureSelectionEnabledForLine({ enabled: false, lineId: currentLineId }));

    return () => {
      dispatch(clearLassoSelection());
    };
  }, []);

  return { getLassoFurnitures, cartoSelectionSpatialFilter: spatialFilters };
};
